/**
 * Creates the session, then hands off to the live screen.
 *
 * The session id is generated here and sent with the create call, which is what makes
 * starting a conversation safe to retry: the Durable Object is keyed by that id, so a
 * retried start resumes the same conversation instead of opening a second one.
 */

import { ApiError, clientUlid } from '@saysooner/api-client';
import { ErrorState, Skeleton } from '@saysooner/ui';
import { useMutation, useQuery } from '@tanstack/react-query';
import type { JSX } from 'react';
import { useEffect, useRef } from 'react';
import { useApi, useApp, useCourseId } from '../app-context.js';
import { retryIf } from '../lib/client.js';
import { useNavigateTo } from '../route-components.js';
import { LiveConversationScreen } from './live.js';

export interface LiveSessionRouteProps {
  readonly kind: 'scenario' | 'open' | 'roleplay';
  readonly refId: string | null;
}

export function LiveSessionRoute({ kind, refId }: LiveSessionRouteProps): JSX.Element {
  const api = useApi();
  const courseId = useCourseId();
  const navigate = useNavigateTo();
  const { requestPaywall } = useApp();

  // Generated once per mount and reused across retries, so a failed start does not
  // strand a half-created session behind an id nobody holds.
  const sessionId = useRef(clientUlid());

  const start = useMutation({
    mutationFn: () =>
      api.call('createSession', {
        body: {
          id: sessionId.current,
          courseId,
          kind,
          refId,
          mode: 'voice',
        },
      }),
  });

  /**
   * The limit, said as a limit: what is spent, and what is still open and still scored.
   * Never a retry — the minutes come back at midnight, not on a second tap.
   */
  const limitReached = (): JSX.Element => {
    requestPaywall('voice_limit');
    return (
      <ErrorState
        title="That's your speaking time for today"
        detail="Review, mistakes and listening are still open, and still scored."
        alternative={{ label: 'Open review', onClick: () => navigate('/review') }}
      />
    );
  };

  // Mount-only in effect: the session id is fixed for the lifetime of this component,
  // so a re-run would be a retry, and retries are the caller's decision. `mutate` is
  // referentially stable, so naming it does not change that.
  const startSession = start.mutate;
  useEffect(() => {
    startSession();
  }, [startSession]);

  if (start.isPending || start.data === undefined) {
    if (start.error !== null) {
      /*
       * A spent quota is not a failure to start, and the server never said it was: it
       * answers `429 quota-exhausted`, `retryable: false`, "You've used today's voice
       * minutes." The branch below for `preflight.problems` was written for this and
       * could never run, because it needs a session that started — and the server
       * refuses to start one. So the learner who reached the free tier's edge, which is
       * the moment this product is built around, got "We couldn't start the
       * conversation" and a Try again that could not work.
       */
      const quota = start.error instanceof ApiError && start.error.isQuota;
      if (quota) return limitReached();

      const problem = start.error instanceof ApiError ? start.error.problem : null;
      return (
        <ErrorState
          title="We couldn't start the conversation"
          // The learner's first question is whether it cost them anything.
          detail={`Nothing was used up.${problem?.detail === undefined ? '' : ` ${problem.detail}`}`}
          {...(problem?.retryable === false ? {} : { onRetry: () => start.mutate() })}
          alternative={{ label: 'Back to Talk', onClick: () => navigate('/talk') }}
        />
      );
    }
    return <Skeleton lines={4} label="Setting up the conversation" />;
  }

  // Voice quota spent mid-start: same destination as the refused create above.
  if (start.data.preflight.problems.includes('quota')) return limitReached();

  return (
    <LiveConversationScreen
      sessionId={sessionId.current}
      courseId={courseId}
      title={kind === 'open' ? 'Open Talk' : 'Conversation'}
      // Forwarded, not defaulted. Without it the screen fell back to `scenario` and
      // every Open Talk, debate and interview event it reports — `open_talk_turn`,
      // `open_talk_end` and their siblings — was emitted under the wrong name or not
      // at all, which is why none of them ever showed a figure.
      kind={kind}
      onNavigate={navigate}
    />
  );
}

/**
 * Joins a conversation that already exists, rather than starting one.
 *
 * Debate, interview and assessment are configured before they begin — a topic, a side,
 * an interview track — so they create the session themselves and hand it over by id.
 * All three navigated here before this route existed, which is to say all three
 * dead-ended at the moment the learner pressed start.
 *
 * The kind comes from the server rather than the URL. It decides the tutor's brief, the
 * rubric and which events the screen reports, and a client that guessed it would be
 * guessing about something the session row already knows.
 */
export function JoinSessionRoute({ sessionId }: { readonly sessionId: string }): JSX.Element {
  const api = useApi();
  const courseId = useCourseId();
  const navigate = useNavigateTo();

  const session = useQuery({
    queryKey: ['session', sessionId],
    queryFn: () => api.call('getSession', { params: { sessionId } }),
    // The row is created a moment before this screen mounts and does not change
    // underneath it; refetching would only risk a flicker mid-conversation.
    staleTime: Number.POSITIVE_INFINITY,
  });

  if (session.isPending) return <Skeleton lines={4} label="Setting up the conversation" />;

  if (session.error !== null || session.data === undefined) {
    return (
      <ErrorState
        title="We couldn't open that conversation"
        detail="Nothing was used up."
        {...retryIf(session.error, () => void session.refetch())}
        alternative={{ label: 'Back to Talk', onClick: () => navigate('/talk') }}
      />
    );
  }

  const kind = session.data.session.kind;
  return (
    <LiveConversationScreen
      sessionId={sessionId}
      courseId={courseId}
      title={TITLES[kind] ?? 'Conversation'}
      // Only the conversational kinds reach this screen. A lesson session has its own
      // screen and its own machine, so it is not in the map and falls back rather than
      // being reported as a debate.
      kind={kind === 'debate' || kind === 'interview' ? kind : 'open'}
      onNavigate={navigate}
    />
  );
}

/** What the learner came here to do, in their words rather than the schema's. */
const TITLES: Partial<Record<string, string>> = {
  debate: 'Debate',
  interview: 'Interview practice',
  assessment: 'Level check',
  open: 'Open Talk',
  roleplay: 'Role-play',
  scenario: 'Conversation',
};
